import type { GameEvent, ViewMessage } from '@basgan/protocol';
import type { Transport, TransportHandlers } from './transport.js';
import { accessToken } from './account.js';

/**
 * Çevrimiçi masa bağlantısı — Cloudflare Durable Object'e ham WebSocket.
 *
 * Colyseus SDK'sı buradan çıktı: Durable Object'in kendi protokolü yok, her
 * mesaj tek bir JSON zarfına giriyor (`{ t: tip, d: yük }`). Mesaj TİPLERİ
 * Colyseus sürümündekilerle birebir aynı bırakıldı, bu yüzden `useGame`
 * tarafındaki işleyiciler hiç değişmedi — değişen yalnızca taşıma katmanı.
 */

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? 'ws://localhost:8787';

/** 4000+ kapanış kodu = sunucu bilerek reddetti, sebep `reason` içinde. */
const REFUSED_FROM = 4000;

const UNREACHABLE = 'Sunucuya ulaşılamadı. Botlarla oynamayı deneyebilirsin.';

function tableUrl(code: string, token: string): string {
  const base = SERVER_URL.replace(/\/+$/, '');
  return `${base}/table?code=${encodeURIComponent(code)}&token=${encodeURIComponent(token)}`;
}

/**
 * Bağlantıyı açar ve masanın KABUL ettiğini doğrular.
 *
 * `open` olayı tek başına yetmiyor: sunucu reddi de el sıkışmayı tamamlayıp
 * ardından kapanış koduyla bildiriyor (tarayıcı başarısız el sıkışmasının
 * sebebini vermediği için). Bu yüzden kabul işareti (`welcome`) ile kapanış
 * yarıştırılıyor — hangisi önce gelirse sonuç o.
 */
function connect(url: string): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      reject(new Error(UNREACHABLE));
      return;
    }

    const settle = (fn: () => void) => {
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onClose);
      socket.removeEventListener('error', onError);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        if ((JSON.parse(event.data) as { t?: string }).t !== 'welcome') return;
      } catch {
        return;
      }
      settle(() => resolve(socket));
    };

    const onClose = (event: CloseEvent) => {
      const refused = event.code >= REFUSED_FROM && event.reason;
      settle(() => reject(new Error(refused ? event.reason : UNREACHABLE)));
    };

    const onError = () => settle(() => reject(new Error(UNREACHABLE)));

    socket.addEventListener('message', onMessage);
    socket.addEventListener('close', onClose);
    socket.addEventListener('error', onError);
  });
}

export async function connectOnline(
  options: { name: string; code: string },
  handlers: TransportHandlers,
): Promise<Transport> {
  const code = options.code.trim().toUpperCase();
  /**
   * Kimlik olarak İMZALI TOKEN gidiyor.
   *
   * Sunucu bunu Supabase'in JWKS'iyle doğruluyor, yani masadaki kimlik
   * istemcinin beyanı değil. Oturum yoksa (hesap sistemi kapalı olan
   * geliştirme kopyası) ada düşülüyor; sunucu o kopyada zaten geliştirme
   * doğrulayıcısıyla çalışıyor, üretimde token'sız bağlantı reddediliyor.
   */
  const token = (await accessToken()) ?? options.name;
  const socket = await connect(tableUrl(code, token));

  /** Bilerek ayrıldık mı — öyleyse kopma uyarısı gösterilmemeli. */
  let left = false;

  socket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    let envelope: { t?: string; d?: unknown };
    try {
      envelope = JSON.parse(event.data) as { t?: string; d?: unknown };
    } catch {
      return; // bozuk çerçeve: sessizce düş
    }

    switch (envelope.t) {
      case 'lobby': {
        const message = envelope.d as {
          players?: { name: string; bot?: boolean }[];
          needed?: number;
          host?: boolean;
        };
        // Salon mesajı geldiyse masa dolu değil demektir; varsa sayımı iptal et.
        // Bot çıkarılınca geri sayımın silinmesi de buradan geliyor.
        handlers.starting(null);
        handlers.lobby(
          (message.players ?? []).map((p) => ({ name: p.name, bot: p.bot === true })),
          message.needed ?? 0,
          message.host === true,
        );
        break;
      }
      case 'starting':
        handlers.starting((envelope.d as { startsAt: number }).startsAt);
        break;
      case 'error':
        handlers.error(envelope.d as string);
        break;
      case 'event':
        handlers.event(envelope.d as GameEvent);
        break;
      case 'view':
        handlers.view(envelope.d as ViewMessage);
        break;
      case 'phrases':
        handlers.phrases((envelope.d as { phrases?: string[] })?.phrases ?? []);
        break;
      default:
        break; // 'welcome' ve tanınmayan tipler
    }
  });

  socket.addEventListener('close', (event) => {
    if (left) return;
    // Sunucu bir sebep bildirdiyse onu göster; yoksa genel kopma uyarısı.
    handlers.error(
      event.code >= REFUSED_FROM && event.reason
        ? event.reason
        : 'Bağlantı koptu. Masaya yeniden girebilirsin.',
    );
  });

  // Dinleyiciler bağlandı; masanın mevcut durumunu şimdi iste. Katılır
  // katılmaz gönderilen mesajlar buraya varmadan kayboluyor.
  socket.send(JSON.stringify({ t: 'hello', d: {} }));

  return {
    send(type, payload) {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ t: type, d: payload }));
    },
    dispose() {
      left = true;
      // 1000 = bilerek ayrılma. Sunucu bunu "masadan kalktı" sayıp koltuğu
      // bota devrediyor; kopma sayılsaydı 60 saniye geri dönüş beklerdi.
      socket.close(1000, 'ayrıldı');
    },
  };
}
